import { CUSTOMER_ROLES } from '@misumi/shared'
import type { CustomerRole } from '@misumi/shared'
import { forwardRef } from 'react'
import type { ChangeEvent, FormEvent } from 'react'
import { FormField } from '../auth/FormField'
import { Select } from '../ui/Select'
import { ROLE_LABELS } from './roleLabels'

export interface InviteUserFormProps {
  readonly email: string
  readonly onEmailChange: (value: string) => void
  readonly emailError?: string | undefined
  readonly role: CustomerRole
  readonly onRoleChange: (value: CustomerRole) => void
  readonly submitting: boolean
  readonly onSubmit: () => void
}

/**
 * Invite-a-teammate form (F0.6, PR-ONB-01) — the visible half of `POST
 * /onboarding/invitations` (S0.9). Fully controlled by `<ManageUsersPage>`
 * (email/role state and the client-side "enter an email" validation error
 * live there, matching the read-then-focus pattern of `<CreateCompanyPage>`)
 * so this component stays presentational and easy to test in isolation.
 *
 * Role is a native `<select>` restricted to `CUSTOMER_ROLES` (`@misumi/shared`)
 * — the same allowlist the server enforces (`isCustomerRole`), so the SPA
 * cannot even construct a request naming a role the server would reject (no
 * staff role exists in this vocabulary, structurally). Co-admin invites
 * (`account_admin` inviting another `account_admin`) are explicitly allowed
 * by the contract, so `account_admin` is not excluded from this list.
 */
export const InviteUserForm = forwardRef<HTMLInputElement, InviteUserFormProps>(
  function InviteUserForm(
    {
      email,
      onEmailChange,
      emailError,
      role,
      onRoleChange,
      submitting,
      onSubmit,
    }: InviteUserFormProps,
    emailInputRef,
  ) {
    function handleSubmit(event: FormEvent<HTMLFormElement>) {
      event.preventDefault()
      onSubmit()
    }

    return (
      <form noValidate onSubmit={handleSubmit} aria-labelledby="invite-user-heading">
        <FormField
          ref={emailInputRef}
          id="invite-email"
          label="Email address"
          type="email"
          autoComplete="email"
          value={email}
          onChange={onEmailChange}
          error={emailError}
          required
        />
        <Select
          id="invite-role"
          name="invite-role"
          label="Role"
          value={role}
          onChange={(event: ChangeEvent<HTMLSelectElement>) =>
            onRoleChange(event.target.value as CustomerRole)
          }
        >
          {CUSTOMER_ROLES.map((candidateRole) => (
            <option key={candidateRole} value={candidateRole}>
              {ROLE_LABELS[candidateRole]}
            </option>
          ))}
        </Select>
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Sending invite…' : 'Send invite'}
          </button>
        </div>
      </form>
    )
  },
)
